import { Badge, Box, Button, Flex, Group, Text } from '@mantine/core';
import { IconPlus, IconPencil, IconPower, IconTool, IconTrash } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SkillProposedAction } from '../chat/types';
import { ProposalDecidedLine } from './ProposalDecidedLine';
import { ProposalDiffBlock } from './ProposalDiffBlock';

type Props = {
  proposal: SkillProposedAction;
  onDecide: (approve: boolean) => void;
  disabled?: boolean;
};

const OPERATION_ICONS = {
  CREATE: IconPlus,
  UPDATE: IconPencil,
  DELETE: IconTrash,
  SET_ENABLED: IconPower,
} as const;

export function SkillProposalWidget({ proposal, onDecide, disabled }: Props) {
  const isPending = proposal.status === 'PENDING' || !proposal.status;
  const OperationIcon = OPERATION_ICONS[proposal.operation];
  const showDiff = proposal.operation !== 'SET_ENABLED' && proposal.diffLines.length > 0;

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center" wrap="nowrap">
          <IconTool size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            Siesta wants to change a skill
          </Text>
          <Badge size="sm" variant="light" color="warning">
            Firm-wide
          </Badge>
        </Group>
        {/* Everything below the summary is computed server-side from the stored proposal,
            never from Siesta's own description of what it did. */}
        <Group gap="xs" align="center" wrap="nowrap">
          <OperationIcon size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Box>
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
              {proposal.summary}
            </Text>
            {proposal.newName && (
              <Text size="textXs" c="gray.7">
                Renames &apos;{proposal.skillName}&apos; to &apos;{proposal.newName}&apos;
              </Text>
            )}
          </Box>
          {showDiff && (
            <Text size="textXs" c="gray.6">
              +{proposal.addedCount} −{proposal.removedCount}
            </Text>
          )}
        </Group>
        {showDiff && (
          <ProposalDiffBlock
            lines={proposal.diffLines}
            truncated={proposal.diffTruncated}
            defaultCollapsed
          />
        )}
        {isPending ? (
          <Group gap="sm">
            <Button
              variant="filled"
              color="orange"
              size="sm"
              onClick={() => onDecide(true)}
              disabled={disabled}
            >
              Approve
            </Button>
            <Button variant="default" size="sm" onClick={() => onDecide(false)} disabled={disabled}>
              Deny
            </Button>
          </Group>
        ) : (
          <ProposalDecidedLine status={proposal.status} failureReason={proposal.failureReason} />
        )}
      </Flex>
    </Box>
  );
}
